import { useFormik } from 'formik'
import { useEffect, useState } from 'react'
import * as Yup from 'yup'
import ImageUpload from '../../components/ImageUpload'
import {
	appAllGetCompliance,
	appCreateCompliance,
	appDeleteCompliance,
	appUpdateCompliance,
	uploadComplianceImage,
} from '../../store/compliances'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import swal from 'sweetalert'
import Select from 'react-select'
import { appAllGetComplianceDate } from '../../store/compliancesDate'
import {
	complianceDocumentExportColumns,
	complianceDueDateExportColumns,
} from '../../config/exportColumns/complianceExportColumns'
import ExportButton from '../../common/ExportButton'

function Compliance({ compliancesList, sansthaDetails }) {
	const dispatch = useDispatch()
	const navigate = useNavigate()

	const [activeTab, setActiveTab] = useState('document')
	const [editMode, setEditMode] = useState(false)
	const [editId, setEditId] = useState(null)
	const [showModal, setShowModal] = useState(false)

	const compliancesDate = useSelector(state => state?.complianceDateReducer)

	//console.log('compliancesDate in component', sansthaDetails)

	const complianceOptions =
		Array.isArray(compliancesDate?.compliancesDate) &&
		compliancesDate.compliancesDate.map(item => ({
			value: item._id,
			label: item.name,
		}))

	useEffect(() => {
		dispatch(appAllGetComplianceDate())
		dispatch(appAllGetCompliance({ sanstha: sansthaDetails?._id }))
	}, [dispatch, sansthaDetails?._id])

	const formik = useFormik({
		initialValues: {
			compliancedate: '',
			file: '',
			filingDate: '',
			frequency: '',
		},
		validationSchema: Yup.object({
			compliancedate: Yup.string().required('Compliance type is required'),
			frequency:
				activeTab === 'document' ? Yup.string().required('Frequency is required') : Yup.string(),
			filingDate:
				activeTab === 'dueDate' ? Yup.string().required('Filing date is required') : Yup.string(),
		}),
		onSubmit: values => {
			const payload = {
				compliancedate: values.compliancedate,
				file: values.file,
				filingDate: values.filingDate,
				frequency: values.frequency,
				sanstha: sansthaDetails?._id,
				complianceduedate: activeTab === 'dueDate',
				navigate,
			}
			const filtered_obj = Object.fromEntries(
				Object.entries(payload).filter(
					([key, value]) =>
						value !== '' && value !== null && value !== undefined && key !== 'navigate',
				),
			)
			if (editMode && editId) {
				dispatch(appUpdateCompliance({ ...filtered_obj, id: editId })).then(() => {
					resetForm()
				})
			} else {
				dispatch(appCreateCompliance(filtered_obj)).then(() => {
					resetForm()
				})
			}
		},
	})

	const resetForm = () => {
		setShowModal(false)
		setEditMode(false)
		setEditId(null)
		formik.resetForm()
		dispatch(appAllGetCompliance({ sanstha: sansthaDetails?._id }))
	}

	const handleEdit = compliance => {
		setEditMode(true)
		setEditId(compliance._id)
		setShowModal(true)
		formik.setValues({
			compliancedate: compliance.compliancedate?._id || '',
			file: compliance.file || '',
			filingDate: compliance.filingDate || '',
			frequency: compliance.frequency || '',
		})
	}

	const handleDelete = id => {
		swal({
			title: 'Are you sure?',
			text: 'You want to delete this Compliance?',
			icon: 'warning',
			buttons: true,
			dangerMode: true,
		}).then(willDelete => {
			if (willDelete) {
				dispatch(appDeleteCompliance(id)).then(() => {
					dispatch(appAllGetCompliance({ sanstha: sansthaDetails?._id }))
				})
			}
		})
	}

	return (
		<div>
			{/* Tabs */}
			<ul className="nav nav-tabs mb-3">
				<li className="nav-item">
					<button
						className={`nav-link ${activeTab === 'document' ? 'active' : ''}`}
						onClick={() => setActiveTab('document')}
					>
						Compliance Document
					</button>
				</li>
				<li className="nav-item">
					<button
						className={`nav-link ${activeTab === 'dueDate' ? 'active' : ''}`}
						onClick={() => setActiveTab('dueDate')}
					>
						Compliance Due Date
					</button>
				</li>
			</ul>

			<div className="mb-3 d-flex justify-content-between align-items-center">
				<h4>
					Total Compliance:{' '}
					{compliancesList?.filter(c =>
						activeTab === 'document' ? !c.complianceduedate : c.complianceduedate,
					).length || 0}
				</h4>

				<div className="d-flex gap-2">
					{activeTab === 'document' ? (
						<ExportButton
							fetchAction={async params => {
								const res = await dispatch(
									appAllGetCompliance({
										...params,
										sanstha: sansthaDetails?._id,
									}),
								)

								const data = res?.payload?.data || res?.payload || []

								const filteredData = data.filter(c => c.complianceduedate === false)

								return {
									...res,
									payload: {
										...res.payload,
										data: filteredData,
									},
								}
							}}
							columns={complianceDocumentExportColumns}
							fileName="Compliance_Documents"
							sheetName="Compliance Documents"
							buttonText="Export Documents"
							buttonClass="btn btn-success"
						/>
					) : (
						<ExportButton
							fetchAction={async params => {
								const res = await dispatch(
									appAllGetCompliance({
										...params,
										sanstha: sansthaDetails?._id,
									}),
								)

								const data = res?.payload?.data || res?.payload || []

								const filteredData = data.filter(c => c.complianceduedate === true)

								return {
									...res,
									payload: {
										...res.payload,
										data: filteredData,
									},
								}
							}}
							columns={complianceDueDateExportColumns}
							fileName="Compliance_Due_Dates"
							sheetName="Compliance Due Dates"
							buttonText="Export Due Dates"
							buttonClass="btn btn-success"
						/>
					)}

					<button
						className="btn btn-primary"
						style={{ marginLeft: '10px' }}
						onClick={() => {
							setShowModal(true)
							setEditMode(false)
							setEditId(null)
							formik.resetForm()
						}}
					>
						<i className="far fa-plus"></i> Add New
					</button>
				</div>
			</div>

			<div className="table-responsive">
				<table className="table table-bordered table-hover">
					<thead>
						<tr>
							<th>Sr. No.</th>
							<th>Document Name</th>
							{/* <th>Periodic Type</th> */}
							{activeTab === 'document' ? (
								<th>Periodic Type</th>
							) : (
								<>
									<th>compliance Date</th>
									<th>Filing Date</th>
								</>
							)}
							{activeTab === 'document' && <th>File</th>}
							{activeTab === 'document' && <th>Date of uploading</th>}
							<th>Actions</th>
						</tr>
					</thead>
					<tbody>
						{compliancesList && compliancesList.length > 0 ? (
							compliancesList
								.filter(compliance =>
									activeTab === 'document'
										? !compliance.complianceduedate
										: compliance.complianceduedate,
								)
								.map((compliance, index) => (
									<tr key={index}>
										<td>{index + 1}</td>
										<td>{compliance.compliancedate?.name || 'N/A'}</td>
										{activeTab === 'document' ? (
											<>
												<td>{compliance.frequency || 'N/A'}</td>
												<td>
													{compliance.file ? (
														compliance.file.toLowerCase().endsWith('.pdf') ? (
															<a
																href={compliance.file}
																download
																target="_blank"
																rel="noopener noreferrer"
																className="btn btn-outline-primary btn-sm"
															>
																<i
																	className="far fa-file-pdf"
																	style={{ fontSize: '20px', color: '#d9534f' }}
																></i>
															</a>
														) : (
															<a
																href={compliance.file}
																target="_blank"
																download
																className="btn btn-outline-primary btn-sm"
															>
																<img
																	src={compliance.file}
																	alt="File"
																	height={70}
																	width={70}
																	style={{
																		display: 'block',
																		marginBottom: '5px',
																		objectFit: 'contain',
																	}}
																/>
															</a>
														)
													) : (
														'N/A'
													)}
												</td>
											</>
										) : (
											<>
												<td>
													{compliance?.compliancedate?.documentDuedate}
													{/* ? new Date(
                                compliance.compliancedate.documentdate
                              ).toLocaleDateString("en-US", {
                                year: "numeric",
                                month: "short",
                                day: "numeric",
                              })
                            : "-"} */}
												</td>
												<td>
													{compliance.filingDate
														? new Date(compliance.filingDate).toLocaleDateString('en-US', {
																year: 'numeric',
																month: 'short',
																day: 'numeric',
														  })
														: '-'}
												</td>
											</>
										)}
										{activeTab === 'document' && (
											<td>
												{new Date(compliance.createdAt).toLocaleDateString('en-US', {
													year: 'numeric',
													month: 'short',
													day: 'numeric',
												})}
											</td>
										)}
										<td>
											<button
												className="btn btn-sm btn-primary mr-2"
												title="Edit"
												onClick={() => handleEdit(compliance)}
											>
												<i className="far fa-edit"></i>
											</button>
											<button
												className="btn btn-sm btn-danger"
												title="Delete"
												onClick={e => {
													e.preventDefault()
													e.stopPropagation()
													handleDelete(compliance._id)
												}}
											>
												<i className="far fa-trash-alt"></i>
											</button>
										</td>
									</tr>
								))
						) : (
							<tr>
								<td colSpan={activeTab === 'document' ? 5 : 4} className="text-center">
									No compliance data available
								</td>
							</tr>
						)}
					</tbody>
				</table>
			</div>

			{/* Modal */}
			{showModal && (
				<div
					className="modal fade show"
					style={{
						display: 'block',
						backgroundColor: 'rgba(0,0,0,0.5)',
						marginTop: '35px',
					}}
				>
					<div className="modal-dialog">
						<div className="modal-content">
							<div className="modal-header">
								<h5 className="modal-title">{editMode ? 'Edit Compliance' : 'Add Compliance'}</h5>
								<button type="button" className="close" onClick={resetForm}>
									<span>&times;</span>
								</button>
							</div>
							<div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
								<form onSubmit={formik.handleSubmit}>
									<div className="row">
										<div className="col-md-6">
											<label>Compliance Type *</label>
											{/* <Select
                        name="compliancedate"
                        options={complianceOptions || []}
                        placeholder="Select compliance..."
                        value={
                          complianceOptions?.find(
                            (opt) => opt.value === formik.values.compliancedate
                          ) || null
                        }
                        onChange={(option) => {
                          formik.setFieldValue("compliancedate", option.value);
                        }}
                        menuPortalTarget={document.body}
                        styles={{
                          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                        }}
                      /> */}
											<Select
												name="compliancedate"
												options={complianceOptions || []}
												placeholder="Select compliance..."
												value={
													complianceOptions?.find(
														opt => opt.value === formik.values.compliancedate,
													) || null
												}
												onChange={option => {
													formik.setFieldValue('compliancedate', option.value)

													//  Find selected compliance object
													const selectedCompliance =
														Array.isArray(compliancesDate?.compliancesDate) &&
														compliancesDate.compliancesDate.find(item => item._id === option.value)

													//  Auto-fill Frequency from API (documentLifetime)
													if (selectedCompliance?.documentLifetime) {
														formik.setFieldValue('frequency', selectedCompliance.documentLifetime)
													} else {
														formik.setFieldValue('frequency', '')
													}
												}}
												menuPortalTarget={document.body}
												styles={{
													menuPortal: base => ({ ...base, zIndex: 9999 }),
												}}
											/>

											{formik.touched.compliancedate && formik.errors.compliancedate && (
												<div className="text-danger">{formik.errors.compliancedate}</div>
											)}
										</div>
										{activeTab === 'document' ? (
											<div className="col-md-6">
												<label>Frequency *</label>
												<select
													className="form-control"
													name="frequency"
													value={formik.values.frequency}
													onChange={formik.handleChange}
													onBlur={formik.handleBlur}
												>
													<option value="">Select option</option>
													<option value="Recurring">Recurring</option>
													{/* <option value="One Time">One Time</option> */}
													<option value="One time">One time</option>
													<option value="Yearly">Yearly</option>
													<option value="Half-yearly">Half-yearly</option>
													<option value="Quarterly">Quarterly</option>
													<option value="Monthly">Monthly</option>
													<option value="Lifetime">Lifetime</option>
												</select>

												{formik.touched.frequency && formik.errors.frequency && (
													<div className="text-danger">{formik.errors.frequency}</div>
												)}
											</div>
										) : (
											<div className="col-md-6">
												<label>Filing Date *</label>
												<input
													type="date"
													className="form-control"
													name="filingDate"
													value={formik.values.filingDate}
													onChange={formik.handleChange}
													onBlur={formik.handleBlur}
												/>
												{formik.touched.filingDate && formik.errors.filingDate && (
													<div className="text-danger">{formik.errors.filingDate}</div>
												)}
											</div>
										)}
										{/* {activeTab === "document" && (
                      <div className="col-md-6">
                        <ImageUpload
                          label="File"
                          uploadFunction={uploadComplianceImage}
                          fieldName="file"
                          value={formik.values.file}
                          onChange={(value) =>
                            formik.setFieldValue("file", value.url)
                          }
                        />
                      </div>
                    )} */}
									</div>

									{/* <div className="row mt-2">
                    {activeTab === "document" ? (
                      <div className="col-md-6">
                        <label>Frequency *</label>
                        <select
                          className="form-control"
                          name="frequency"
                          value={formik.values.frequency}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                        >
                          <option value="">Select option</option>
                          <option value="Recurring">Recurring</option>
                          <option value="One time">One time</option>
                          <option value="Yearly">Yearly</option>
                          <option value="Half-yearly">Half-yearly</option>
                          <option value="Quarterly">Quarterly</option>
                          <option value="Monthly">Monthly</option>
                          <option value="Lifetime">Lifetime</option>
                        </select>

                        {formik.touched.frequency &&
                          formik.errors.frequency && (
                            <div className="text-danger">
                              {formik.errors.frequency}
                            </div>
                          )}
                      </div>
                    ) : (
                      <div className="col-md-6">
                        <label>Filing Date *</label>
                        <input
                          type="date"
                          className="form-control"
                          name="filingDate"
                          value={formik.values.filingDate}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                        />
                        {formik.touched.filingDate &&
                          formik.errors.filingDate && (
                            <div className="text-danger">
                              {formik.errors.filingDate}
                            </div>
                          )}
                      </div>
                    )}
                  </div> */}
								</form>
							</div>

							<div className="modal-footer">
								<button className="btn btn-secondary" onClick={resetForm}>
									Cancel
								</button>
								<button className="btn btn-primary" onClick={formik.handleSubmit}>
									Save
								</button>
							</div>
						</div>
					</div>
				</div>
			)}
		</div>
	)
}
export default Compliance
